import { PlusCircleIcon } from "@heroicons/react/24/outline";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useRouter } from "next/router";
import React, { Fragment } from "react";
import { type ICharacter } from "~/data/useCharacterData";
import { env } from "~/env.mjs";
import useUser from "~/lib/useUser";
import { InventoryItem, InventoryItemSkeleton } from "./InventoryItem";

type ICharacterRecent = {
  id: string;
  userId: string;
  externalId: string;
  name: string;
  voiceType: string;
  backStory: string | null;
  greeting: string;
  facingDescription: string;
  createdAt: string;
  updatedAt: string;
  deleted: boolean;
  deletedAt: string | null;
  visibilityType: string;
  category: string;
  modelLink: string;
  modelProvider: string;
  modelPreview: string;
  modelPlaceholder: string | null;
  object: any | null;
  instructions: string;
  model: any | null;
  tools: string[];
  metadata: any[];
  provider: string;
  gender: "male" | "female" | "other";
  modelId: string;
};

const RecentNPCsInventory: React.FC<{
  mockAddress?: string;
  itemProps?: {
    actionsComponent: (props: Partial<ICharacter>) => React.ReactNode;
  };
}> = ({ ...props }) => {
  const { itemProps } = props;
  const { user } = useUser();
  const { push } = useRouter();

  const queryFn = async () => {
    const url = `${env.NEXT_PUBLIC_PORTAL_API_URL}/api/v2/assistant/list/recent`;
    const { data } = await axios.get<ICharacterRecent[]>(url, {
      headers: {
        Authorization: `Bearer ${user?.accessToken || ""}`,
      },
    });
    return data;
  };

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["character", "recents"],
    queryFn,
    enabled: !!user?.accessToken,
  });

  return (
    <div>
      <div className="space-y-6">
        <h2 className="text-lg font-medium leading-6 text-light-50">
          Continue chatting
        </h2>
        <div
          className={
            "grid min-h-[208px] grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8"
          }
        >
          {isLoading || isFetching ? (
            <>
              {Array.from(Array(5).keys()).map((i) => (
                <InventoryItemSkeleton key={i} />
              ))}
            </>
          ) : (
            <>
              {data?.map((item, i) => {
                return (
                  <Fragment key={i}>
                    <InventoryItem
                      assistantId={item.id}
                      assistantName={item.name}
                      facingDescription={item.facingDescription}
                      modelPreview={item.modelPreview}
                      isLast={false}
                      key={item.id}
                      {...itemProps}
                    />
                  </Fragment>
                );
              })}
              <div className="rounded-lg bg-primary-100 text-sm font-semibold hover:brightness-90 hover:saturate-[.9]">
                <button
                  onClick={() => push("/ai-npcs/create")}
                  className="flex h-full w-full flex-col items-center justify-center gap-6 text-dark-250"
                >
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 brightness-95 saturate-[.95]">
                    <PlusCircleIcon className="h-6 w-6" />
                  </div>
                  <span>Create a new AI NPC</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecentNPCsInventory;
